import { SettingOutlined, SwapOutlined } from "@ant-design/icons";
import { Badge, Button, Popover, Tooltip } from "antd";
import OffsetIcon from "components/svg-icons/offset-icon";
import { TLinkSource } from "components/feature-entity-linking-page/models";
import { useState } from "react";

type ActivityOffsetType = {
  opt_title: string;
  opt_subtitle: string;
  opt_img: React.ReactNode;
  opt_isDisabled: boolean;
  opt_isSelected: boolean;
  onChangeAction: () => void;
  testId?: string;
};

export default function ActivityCardSetting({
  activityLinkedItem,
  openSettings,
  setOpenSettings,
  onOffsetChange,
  updateReverseLinking
}: {
  activityLinkedItem: TLinkSource;
  openSettings: boolean;
  setOpenSettings: (open: boolean) => void;
  onOffsetChange: (prop: {
    offset: number | null;
    activityLink: TLinkSource;
  }) => void;
  updateReverseLinking: (prop: { activityLink: TLinkSource }) => void;
}) {
  const [open, setOpen] = useState(openSettings);
  const isOffsetSet = !(
    activityLinkedItem.offset_duration === null ||
    activityLinkedItem.offset_duration === 0
  );

  const menuItem = (option: ActivityOffsetType) => {
    return (
      <Tooltip
        title={
          option.opt_isDisabled
            ? "Only one setting can be applied at a time."
            : ""
        }
      >
        <div className="flex">
          <Button
            type="text"
            className="flex text-left space-x-1 h-14 w-full hover:border-transparent pb-2 hover:bg-black hover:bg-opacity-5  disabled:opacity-40 disabled:bg-ciq-gray-500"
            onClick={option.onChangeAction}
            disabled={option.opt_isDisabled}
            data-testid={
              option.testId ||
              `activity-card-setting-button-${
                isOffsetSet ? "remove-offset" : "add-offset"
              }`
            }
          >
            <Badge
              status="default"
              offset={[-2, 4]}
              dot={option.opt_isSelected}
              data-testid="activity-card-setting-badge"
            >
              {option.opt_img}
            </Badge>
            <div
              className="flex-col"
              data-testid="activity-card-setting-content-text"
            >
              <div
                className="text-black"
                data-testid="activity-card-setting-title"
              >
                {option.opt_title}
              </div>
              <div
                className="text-ciq-gray-700 text-xs"
                data-testid="activity-card-setting-subtitle"
              >
                {option.opt_subtitle}
              </div>
            </div>
          </Button>
        </div>
      </Tooltip>
    );
  };

  return (
    <Popover
      open={open}
      trigger="click"
      onOpenChange={(visible) => {
        setOpen(visible);
      }}
      className="px-1 flex"
      placement="bottom"
      overlayClassName="popoverNoPadding"
      overlayInnerStyle={{ borderRadius: "6px" }}
      data-testid="activity-card-setting-popover"
      content={
        <div className="flex flex-col">
          <div
            className="px-4 py-3 opacity-40 font-semibold text-xs"
            data-testid="advanced-settings-title"
          >
            Advanced Settings
          </div>
          {menuItem({
            opt_title: isOffsetSet ? "Remove Offset" : "Add Offset",
            opt_subtitle: isOffsetSet
              ? "Make Activity the Predecessor for calculations."
              : "Add or Subtract a duration to offset the date.",
            opt_img: <OffsetIcon />,
            opt_isDisabled: activityLinkedItem.reverse_dependency,
            opt_isSelected: isOffsetSet,
            testId: `activity-card-setting-button-${
              isOffsetSet ? "remove-offset" : "add-offset"
            }`,
            onChangeAction: () => {
              if (isOffsetSet) {
                onOffsetChange({
                  offset: null,
                  activityLink: activityLinkedItem
                });
              }
              if (setOpenSettings) {
                if (isOffsetSet) {
                  setOpenSettings(false);
                } else {
                  setOpenSettings(true);
                }
              }
              setOpen(false);
            }
          })}
          {menuItem({
            opt_title: activityLinkedItem.reverse_dependency
              ? "Remove Reversed Dependency"
              : "Reverse Dependency",
            opt_subtitle: activityLinkedItem.reverse_dependency
              ? "Revert to using Material as the Predecessor."
              : "Make Activity the Predecessor for calculations.",
            opt_img: <SwapOutlined className="text-ciq-gray-550 z-10" />,
            opt_isDisabled: isOffsetSet,
            opt_isSelected: activityLinkedItem.reverse_dependency,
            testId: `activity-card-setting-button-${
              activityLinkedItem.reverse_dependency
                ? "remove-reverse-dependency"
                : "reverse-dependency"
            }`,
            onChangeAction: () => {
              onOffsetChange({
                offset: null,
                activityLink: activityLinkedItem
              });
              setOpenSettings(false);
              setOpen(false);
              updateReverseLinking({ activityLink: activityLinkedItem });
            }
          })}
        </div>
      }
    >
      <Badge
        status="default"
        offset={[-2, 2]}
        dot={
          !!(
            activityLinkedItem.reverse_dependency ||
            activityLinkedItem.offset_duration
          )
        }
        data-testid="activity-card-setting-trigger-badge"
      >
        <SettingOutlined
          title="Advanced Settings"
          className="cursor-pointer z-10"
          data-testid="activity-card-setting-trigger-icon"
        />
      </Badge>
    </Popover>
  );
}
